import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import './DiaryDatePicker.css';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// Generate month data: { year, month, weeks[] }
function buildMonth(year, month) {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const cells = [];
    for (let i = firstDay - 1; i >= 0; i--) {
        cells.push({ day: daysInPrevMonth - i, current: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(year, month, d);
        date.setHours(0, 0, 0, 0);
        cells.push({ day: d, current: true, date });
    }
    const remaining = 7 - (cells.length % 7);
    if (remaining < 7) {
        for (let d = 1; d <= remaining; d++) {
            cells.push({ day: d, current: false });
        }
    }

    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) {
        weeks.push(cells.slice(i, i + 7));
    }
    return { year, month, label: `${MONTH_NAMES[month]} ${year}`, weeks };
}

// Build N months starting from a given year/month
function buildMonths(startYear, startMonth, count) {
    const months = [];
    let y = startYear, m = startMonth;
    for (let i = 0; i < count; i++) {
        months.push(buildMonth(y, m));
        m++;
        if (m > 11) { m = 0; y++; }
    }
    return months;
}

class DiaryDatePicker extends Component {
    constructor(props) {
        super(props);
        const selected = props.selected ? new Date(props.selected) : new Date();
        // Start 3 months before selected, show 12 months total
        let startMonth = selected.getMonth() - 3;
        let startYear = selected.getFullYear();
        if (startMonth < 0) { startMonth += 12; startYear--; }

        this.state = {
            isMobile: typeof window !== 'undefined' && window.innerWidth < 768,
            months: buildMonths(startYear, startMonth, 12),
            startYear,
            startMonth,
        };
        this.overlayRef = React.createRef();
        this.scrollRef = React.createRef();
        this.isExtending = false;
    }

    componentDidMount() {
        this._resizeHandler = () => {
            this.setState({ isMobile: window.innerWidth < 768 });
        };
        window.addEventListener('resize', this._resizeHandler);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._resizeHandler);
    }

    componentDidUpdate(prevProps) {
        // When opening, reset months around selected date and scroll to it
        if (!prevProps.open && this.props.open) {
            const selected = this.props.selected ? new Date(this.props.selected) : new Date();
            let startMonth = selected.getMonth() - 3;
            let startYear = selected.getFullYear();
            if (startMonth < 0) { startMonth += 12; startYear--; }
            const months = buildMonths(startYear, startMonth, 12);
            this.setState({ months, startYear, startMonth }, () => {
                // Scroll to selected month
                if (this.scrollRef.current) {
                    const idx = this.state.months.findIndex(
                        m => m.year === selected.getFullYear() && m.month === selected.getMonth()
                    );
                    if (idx >= 0) {
                        const monthEls = this.scrollRef.current.querySelectorAll('.ddp-month-block');
                        if (monthEls[idx]) {
                            monthEls[idx].scrollIntoView({ block: 'start' });
                        }
                    }
                }
            });
        }
    }

    handleScroll = () => {
        if (!this.scrollRef.current || this.isExtending) return;
        const el = this.scrollRef.current;

        // Prepend months when near top
        if (el.scrollTop < 200) {
            this.isExtending = true;
            const prevHeight = el.scrollHeight;
            let { startYear, startMonth } = this.state;
            // Go back 6 months
            startMonth -= 6;
            if (startMonth < 0) { startMonth += 12; startYear--; }
            const newMonths = buildMonths(startYear, startMonth, 6);
            this.setState(prev => ({
                months: [...newMonths, ...prev.months],
                startYear,
                startMonth,
            }), () => {
                // Maintain scroll position
                const newHeight = el.scrollHeight;
                el.scrollTop += newHeight - prevHeight;
                this.isExtending = false;
            });
        }

        // Append months when near bottom
        if (el.scrollTop + el.clientHeight > el.scrollHeight - 200) {
            this.isExtending = true;
            const last = this.state.months[this.state.months.length - 1];
            let nextMonth = last.month + 1;
            let nextYear = last.year;
            if (nextMonth > 11) { nextMonth = 0; nextYear++; }
            const newMonths = buildMonths(nextYear, nextMonth, 6);
            this.setState(prev => ({
                months: [...prev.months, ...newMonths],
            }), () => {
                this.isExtending = false;
            });
        }
    }

    handleDayClick = (date) => {
        if (this.props.onChange) {
            this.props.onChange(date);
        }
    }

    handleOverlayClick = (e) => {
        if (e.target === this.overlayRef.current && this.props.onClose) {
            this.props.onClose();
        }
    }

    handleTodayClick = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        this.handleDayClick(today);
    }

    renderMonthBlock(monthData, key) {
        const selected = this.props.selected ? new Date(this.props.selected) : null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return (
            <div className="ddp-month-block" key={key}>
                <div className="ddp-month-title">{monthData.label}</div>
                <div className="ddp-day-labels">
                    {DAY_LABELS.map(d => <span key={d} className="ddp-day-label">{d}</span>)}
                </div>
                <div className="ddp-grid">
                    {monthData.weeks.map((week, wi) => (
                        <div key={wi} className="ddp-week">
                            {week.map((cell, ci) => {
                                const isToday = cell.date && cell.date.getTime() === today.getTime();
                                const isSelected = cell.date && selected &&
                                    cell.date.getFullYear() === new Date(selected).getFullYear() &&
                                    cell.date.getMonth() === new Date(selected).getMonth() &&
                                    cell.date.getDate() === new Date(selected).getDate();
                                return (
                                    <button
                                        key={ci}
                                        type="button"
                                        className={
                                            'ddp-cell' +
                                            (!cell.current ? ' ddp-cell--other' : '') +
                                            (isToday ? ' ddp-cell--today' : '') +
                                            (isSelected ? ' ddp-cell--selected' : '')
                                        }
                                        disabled={!cell.current}
                                        onClick={() => cell.date && this.handleDayClick(cell.date)}
                                    >
                                        {cell.day}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    renderScrollableCalendar() {
        return (
            <div
                className="ddp-scroll-container"
                ref={this.scrollRef}
                onScroll={this.handleScroll}
            >
                {this.state.months.map((m, i) => this.renderMonthBlock(m, `${m.year}-${m.month}`))}
            </div>
        );
    }

    renderDesktop() {
        if (!this.props.open) return null;

        // Single month with arrows for desktop
        const selected = this.props.selected ? new Date(this.props.selected) : new Date();
        const viewMonth = this.state.months.find(
            m => m.year === selected.getFullYear() && m.month === selected.getMonth()
        ) || this.state.months[3];

        return ReactDOM.createPortal(
            <React.Fragment>
                <div className="ddp-desktop-backdrop" onClick={this.props.onClose} />
                <div className="ddp-desktop-dropdown" style={this.getDesktopPosition()}>
                    <div className="ddp-desktop-scroll" ref={this.scrollRef} onScroll={this.handleScroll}>
                        {this.state.months.map((m, i) => this.renderMonthBlock(m, `${m.year}-${m.month}`))}
                    </div>
                    <div className="ddp-footer">
                        <button type="button" className="ddp-today-link" onClick={this.handleTodayClick}>Today</button>
                    </div>
                </div>
            </React.Fragment>,
            document.body
        );
    }

    getDesktopPosition() {
        if (!this.props.anchorEl) return { top: 100, left: '50%', transform: 'translateX(-50%)' };
        const rect = this.props.anchorEl.getBoundingClientRect();
        return {
            position: 'fixed',
            top: rect.bottom + 8,
            left: rect.left + rect.width / 2,
            transform: 'translateX(-50%)',
        };
    }

    renderMobile() {
        if (!this.props.open) return null;
        return ReactDOM.createPortal(
            <div className="ddp-mobile-overlay" ref={this.overlayRef} onClick={this.handleOverlayClick}>
                <div className="ddp-mobile-sheet">
                    <div className="ddp-sheet-header">
                        <span className="ddp-sheet-title">Select Date</span>
                        <button type="button" className="ddp-sheet-close" onClick={this.props.onClose}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                            </svg>
                        </button>
                    </div>
                    <div className="ddp-sheet-divider" />
                    {this.renderScrollableCalendar()}
                    <div className="ddp-sheet-footer">
                        <button type="button" className="ddp-sheet-cancel" onClick={this.props.onClose}>Cancel</button>
                        <button type="button" className="ddp-sheet-today" onClick={this.handleTodayClick}>Today</button>
                    </div>
                </div>
            </div>,
            document.body
        );
    }

    render() {
        if (this.state.isMobile) return this.renderMobile();
        return this.renderDesktop();
    }
}

export default DiaryDatePicker;
